/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  USESOUNDEFFECTS.TS - Audio Feedback System                               ║
 * ║  Dopamine-inducing sounds for purchases, wins, and game events            ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useCallback, useEffect, useRef, useState, createContext, useContext, ReactNode } from 'react';

// Sound URLs - using Web Audio API with generated tones for reliability
// Can be replaced with actual audio files later

type SoundType = 
  | 'purchase'      // Ka-ching on key buy
  | 'purchaseWhale' // Big purchase sound
  | 'timerWarning'  // Tick-tock when timer low
  | 'timerCritical' // Urgent alarm when < 1 min
  | 'roundEnd'      // Victory fanfare
  | 'dividends'     // Cash register for claims
  | 'reaper'        // Dark ominous sound
  | 'bloodMoon'     // Spooky ambiance
  | 'extinction'    // Explosion/rumble
  | 'ghostKey'      // Ethereal whoosh
  | 'combo'         // Power-up sound
  | 'notification'  // General alert
  | 'error'         // Error buzz
  | 'click';        // UI click

interface SoundSettings {
  enabled: boolean;
  volume: number; // 0-1
  musicEnabled: boolean;
  musicVolume: number;
}

const DEFAULT_SETTINGS: SoundSettings = {
  enabled: true,
  volume: 0.5,
  musicEnabled: false,
  musicVolume: 0.3,
};

// Singleton audio context
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

// Generate tones using Web Audio API
function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.5,
  attack: number = 0.01,
  _decay: number = 0.1
): void {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    // Envelope
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + attack);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (e) {
    console.log('Audio playback failed:', e);
  }
}

// Sound effect definitions
const SOUND_DEFINITIONS: Record<SoundType, () => void> = {
  purchase: () => {
    // Ka-ching! Two-tone coin sound
    playTone(800, 0.1, 'sine', 0.3);
    setTimeout(() => playTone(1200, 0.15, 'sine', 0.4), 50);
    setTimeout(() => playTone(1600, 0.2, 'triangle', 0.3), 100);
  },
  
  purchaseWhale: () => {
    // Big bass drop + sparkle
    playTone(100, 0.3, 'sawtooth', 0.5);
    playTone(200, 0.25, 'sawtooth', 0.4);
    setTimeout(() => {
      playTone(800, 0.1, 'sine', 0.3);
      playTone(1000, 0.1, 'sine', 0.3);
      playTone(1200, 0.15, 'sine', 0.4);
    }, 150);
  },
  
  timerWarning: () => {
    // Tick-tock
    playTone(440, 0.05, 'square', 0.2);
  },
  
  timerCritical: () => {
    // Urgent alarm
    playTone(880, 0.1, 'square', 0.3);
    setTimeout(() => playTone(660, 0.1, 'square', 0.3), 150);
  },
  
  roundEnd: () => {
    // Victory fanfare - ascending arpeggio
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    notes.forEach((freq, i) => {
      setTimeout(() => playTone(freq, 0.3, 'triangle', 0.4), i * 100);
    });
    // Final chord
    setTimeout(() => {
      playTone(523, 0.5, 'sine', 0.3);
      playTone(659, 0.5, 'sine', 0.3);
      playTone(784, 0.5, 'sine', 0.3);
      playTone(1047, 0.5, 'sine', 0.4);
    }, 400);
  },
  
  dividends: () => {
    // Cash register - multiple coins
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        playTone(1200 + Math.random() * 400, 0.08, 'sine', 0.2);
      }, i * 40);
    }
  },
  
  reaper: () => {
    // Dark ominous
    playTone(80, 0.5, 'sawtooth', 0.4);
    playTone(85, 0.5, 'sawtooth', 0.3);
    setTimeout(() => playTone(60, 0.3, 'square', 0.2), 200);
  },
  
  bloodMoon: () => {
    // Ethereal dark ambiance
    playTone(220, 0.8, 'sine', 0.2);
    playTone(277, 0.8, 'sine', 0.15);
    playTone(330, 0.8, 'sine', 0.15);
  },
  
  extinction: () => {
    // Explosion/rumble
    playTone(60, 0.4, 'sawtooth', 0.5);
    playTone(80, 0.35, 'sawtooth', 0.4);
    playTone(100, 0.3, 'sawtooth', 0.3);
    setTimeout(() => playTone(40, 0.5, 'sawtooth', 0.3), 100);
  },
  
  ghostKey: () => {
    // Ethereal whoosh
    for (let i = 0; i < 10; i++) {
      setTimeout(() => {
        playTone(400 + i * 100, 0.1, 'sine', 0.1 - i * 0.01);
      }, i * 30);
    }
  },
  
  combo: () => {
    // Power-up ascending
    const freqs = [400, 500, 600, 800];
    freqs.forEach((f, i) => {
      setTimeout(() => playTone(f, 0.1, 'square', 0.3), i * 50);
    });
  },
  
  notification: () => {
    // Gentle ping
    playTone(880, 0.1, 'sine', 0.3);
    setTimeout(() => playTone(1100, 0.15, 'sine', 0.2), 100);
  },
  
  error: () => {
    // Error buzz
    playTone(200, 0.15, 'square', 0.3);
    setTimeout(() => playTone(150, 0.2, 'square', 0.3), 100);
  },
  
  click: () => {
    // Soft click
    playTone(600, 0.03, 'sine', 0.2);
  },
};

export function useSoundEffects() {
  const [settings, setSettings] = useState<SoundSettings>(() => {
    try {
      const saved = localStorage.getItem('nt3d_sound_settings');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Save settings
  useEffect(() => {
    localStorage.setItem('nt3d_sound_settings', JSON.stringify(settings));
  }, [settings]);

  // Play a sound effect
  const play = useCallback((sound: SoundType) => {
    if (!settings.enabled) return;
    
    try {
      SOUND_DEFINITIONS[sound]();
    } catch (e) {
      console.log('Sound play failed:', e);
    }
  }, [settings.enabled]);

  // Start timer warning sounds
  const startTimerWarning = useCallback((timeRemaining: number) => {
    if (!settings.enabled) return;
    
    // Clear existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    if (timeRemaining <= 0) return;

    // Determine interval based on urgency
    let interval: number;
    let soundType: SoundType;
    
    if (timeRemaining <= 60) {
      interval = 500; // Every 0.5s
      soundType = 'timerCritical';
    } else if (timeRemaining <= 300) {
      interval = 1000; // Every 1s
      soundType = 'timerWarning';
    } else {
      return; // No warning needed
    }

    timerIntervalRef.current = setInterval(() => {
      play(soundType);
    }, interval);
  }, [settings.enabled, play]);

  // Stop timer warnings
  const stopTimerWarning = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Settings controls
  const toggleSound = useCallback(() => {
    setSettings(s => ({ ...s, enabled: !s.enabled }));
  }, []);

  const setVolume = useCallback((volume: number) => {
    setSettings(s => ({ ...s, volume: Math.max(0, Math.min(1, volume)) }));
  }, []);

  const toggleMusic = useCallback(() => {
    setSettings(s => ({ ...s, musicEnabled: !s.musicEnabled }));
  }, []);

  return {
    play,
    settings,
    toggleSound,
    setVolume,
    toggleMusic,
    startTimerWarning,
    stopTimerWarning,
  };
}

// Context for global sound access
import { createElement } from 'react';

interface SoundContextType {
  play: (sound: SoundType) => void;
  settings: SoundSettings;
  toggleSound: () => void;
  setVolume: (v: number) => void;
}

const SoundContext = createContext<SoundContextType | null>(null);

export function SoundProvider({ children }: { children: ReactNode }) {
  const soundHook = useSoundEffects();
  
  return createElement(
    SoundContext.Provider,
    {
      value: {
        play: soundHook.play,
        settings: soundHook.settings,
        toggleSound: soundHook.toggleSound,
        setVolume: soundHook.setVolume,
      }
    },
    children
  );
}

export function useSound() {
  const context = useContext(SoundContext);
  if (!context) {
    // Return a no-op version if not in provider
    return {
      play: () => {},
      settings: DEFAULT_SETTINGS,
      toggleSound: () => {},
      setVolume: () => {},
    };
  }
  return context;
}

export type { SoundType, SoundSettings };
